import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { EdLogo } from "../shell/EdLogo";
import { trackCtaClick } from "../../lib/analytics";

type ToolKey = "Releases" | "Flags" | "API mocks" | "Webhooks";

const NAV_LINKS: { label: ToolKey; to: string }[] = [
  { label: "Releases", to: "/releases" },
  { label: "Flags", to: "/flags" },
  { label: "API mocks", to: "/mocks" },
  { label: "Webhooks", to: "/webhooks" },
];

export function MarketingNav({ active }: { active: ToolKey }) {
  return (
    <header className="border-b border-rule">
      <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="inline-flex">
          <EdLogo size={17} />
        </Link>
        <nav className="hidden md:flex gap-7 text-sm text-muted-foreground items-center">
          {NAV_LINKS.map((l) => {
            const isActive = l.label === active;
            return (
              <Link
                key={l.label}
                to={l.to}
                className={`relative ${isActive ? "text-foreground font-semibold" : ""}`}
              >
                {l.label}
                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-[22px] h-0.5 bg-foreground" />
                )}
              </Link>
            );
          })}
        </nav>
        <div className="flex gap-3 items-center">
          <Link
            to="/login"
            className="text-sm"
            onClick={() =>
              trackCtaClick("header", { target: "login", surface: active })
            }
          >
            Sign in
          </Link>
          <Link
            to="/register"
            onClick={() =>
              trackCtaClick("header", { target: "register", surface: active })
            }
          >
            <Button size="sm">Start free →</Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

export function MarketingBeta() {
  return (
    <div className="bg-signal-red-soft text-foreground border-b border-rule">
      <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-2.5 text-xs sm:text-sm">
        <span>
          <b className="text-signal-red">● Beta</b> &nbsp; Free for every team
          and project.{" "}
          <span className="font-serif italic">No card. No caps.</span>
        </span>
      </div>
    </div>
  );
}

export function MarketingFooter() {
  return (
    <footer>
      <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-5 font-mono text-xs text-faint">
        <span>
          Swisstools — five tools, one tab. Apologies to your bookmarks bar.
        </span>
      </div>
    </footer>
  );
}

const TOOL_META: {
  n: string;
  t: ToolKey;
  d: string;
  color: string;
  to: string;
}[] = [
  {
    n: "01",
    t: "Releases",
    d: "AI-drafted notes for humans.",
    color: "var(--color-signal-red)",
    to: "/releases",
  },
  {
    n: "02",
    t: "Flags",
    d: "Toggles anyone can flip.",
    color: "var(--color-signal-ok)",
    to: "/flags",
  },
  {
    n: "03",
    t: "API mocks",
    d: "Fake endpoints, real URLs.",
    color: "var(--color-signal-warn)",
    to: "/mocks",
  },
  {
    n: "04",
    t: "Webhooks",
    d: "See it, replay it, bypass it.",
    color: "var(--color-signal-lavender)",
    to: "/webhooks",
  },
];

export function CrossSell({ except }: { except: ToolKey }) {
  const tools = TOOL_META.filter((t) => t.t !== except);
  return (
    <section className="border-t border-rule">
      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 sm:grid-cols-3">
        {tools.map((c, i) => (
          <Link
            key={c.n}
            to={c.to}
            className={`block p-6 sm:p-7 transition-colors hover:bg-card-paper ${
              i < tools.length - 1 ? "sm:border-r" : ""
            } border-rule`}
          >
            <span
              className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase"
              style={{ color: c.color }}
            >
              <span
                className="size-1.5 rounded-sm"
                style={{ background: c.color }}
              />
              {c.n} · {c.t}
            </span>
            <div className="font-serif text-xl font-semibold tracking-tight mt-2 mb-1">
              {c.t}
            </div>
            <div className="text-sm leading-relaxed text-muted-foreground">
              {c.d}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export type ValueItem = { tag: string; title: string; body: string };

export function ValueGrid({
  items,
  accent,
}: {
  items: ValueItem[];
  accent: string;
}) {
  return (
    <section className="border-t border-rule">
      <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0">
        {items.map((it, i) => (
          <div
            key={it.tag}
            className={`md:px-7 ${i === 0 ? "md:pl-0" : ""} ${
              i === items.length - 1 ? "md:pr-0" : ""
            } ${i < items.length - 1 ? "md:border-r" : ""} border-rule`}
          >
            <div
              className="font-mono text-[10px] tracking-[0.14em] uppercase mb-2.5"
              style={{ color: accent }}
            >
              {String(i + 1).padStart(2, "0")} · {it.tag}
            </div>
            <div className="font-serif text-[22px] font-semibold tracking-tight leading-tight mb-2">
              {it.title}
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground m-0">
              {it.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MarketingShellLayout({
  active,
  children,
}: {
  active: ToolKey;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-paper text-foreground flex flex-col">
      <MarketingNav active={active} />
      <MarketingBeta />
      <main className="flex-1">{children}</main>
      <CrossSell except={active} />
      <MarketingFooter />
    </div>
  );
}
